import React, { useState, useEffect, useCallback } from 'react';
import PaymentHoldOverlay from '../components/business/PaymentHoldOverlay';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { resolveAssetUrl, itemImages } from '../lib/assets';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ImageLightbox } from '../components/business/ImageLightbox';
import { CalendarClock, Clock, Gift, Phone, Loader2, Check, Sparkles, User, Star, HelpCircle, Mail, Send, CreditCard, Globe, Facebook, Instagram } from 'lucide-react';
import { pluginsFor, orderedSections, pluginEnabled } from '../data/businessCatalog';
import { pageVars } from '../data/motifs';
import ContactBusinessButton from '../components/ContactBusinessButton';
import { HoursMapSection, PromotionsSection } from '../components/business/PublicSections';
import BusinessMenuPage from './BusinessMenuPage';
import { useAuth } from '../contexts/AuthContext';
import { isValidPhone, PHONE_ERROR } from '../lib/phone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;
const INPUT_CLS = 'bg-[var(--bg-input)] border-[var(--border-input)] text-[var(--text-primary)] placeholder:text-[var(--text-faint)]';
const UNIT_LV = { hour: '/ stundā', group: '/ grupai', package: 'komplekts', night: '/ naktī', month: '/ mēnesī', none: '' };
const fmtEur = (n) => Number(n).toLocaleString('lv-LV', { maximumFractionDigits: 2 });
const KIND_LABELS = {
  general:    { cta: 'Rezervēt laiku', section: 'Rezervē savu laiku', dialog: 'Rezervācijas pieprasījums', price: 'Cenas' },
  salon:      { cta: 'Pieteikties vizītei', section: 'Piesakies vizītei', dialog: 'Pieraksts vizītei', price: 'Pakalpojumi & cenas' },
  health:     { cta: 'Pieteikties vizītei', section: 'Piesakies pie speciālista', dialog: 'Pieraksts vizītei', price: 'Pakalpojumi & cenas' },
  fitness:    { cta: 'Pieteikties nodarbībai', section: 'Piesakies nodarbībai', dialog: 'Pieteikšanās nodarbībai', price: 'Nodarbības & cenas' },
  auto:       { cta: 'Pieteikt servisu', section: 'Piesaki auto servisam', dialog: 'Servisa pieprasījums', price: 'Pakalpojumi & cenas' },
  education:  { cta: 'Pieteikties kursam', section: 'Piesakies kursam', dialog: 'Pieteikšanās', price: 'Kursi & cenas' },
  stay:       { cta: 'Rezervēt uzturēšanos', section: 'Rezervē savu uzturēšanos', dialog: 'Rezervācijas pieprasījums', price: 'Numuri & cenas' },
  trade:      { cta: 'Pieprasīt piedāvājumu', section: 'Pieprasi piedāvājumu vai atzvanu', dialog: 'Pieprasījums', price: 'Pakalpojumi & cenas' },
  realestate: { cta: 'Pieteikt apskati', section: 'Piesaki objekta apskati', dialog: 'Apskates pieprasījums', price: 'Piedāvājumi' },
};

export default function BusinessReservationsPage({ data: preData, userId: preUserId, pageId: prePageId } = {}) {
  const { user } = useAuth();
  const params = useParams();
  const [sp, setSp] = useSearchParams();
  const userId = preUserId || params.userId;
  const pageId = prePageId || params.pageId || null;
  const [data, setData] = useState(preData || null);
  const [loading, setLoading] = useState(!preData);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [date, setDate] = useState(new Date());
  const [slot, setSlot] = useState(null);
  const [service, setService] = useState(null);
  const [specialistId, setSpecialistId] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', email: '', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [taken, setTaken] = useState([]);
  const [dayAvailable, setDayAvailable] = useState(true);
  const [confirmCode, setConfirmCode] = useState(null);
  // Payment confirmation state (post-payment review prompt removed — a review
  // must not be possible for a deal that isn't completed yet).
  const [payChecking, setPayChecking] = useState(false);
  // Gift card purchase
  const [giftOpen, setGiftOpen] = useState(false);
  const [giftIdx, setGiftIdx] = useState(null);          // predefined card index, or null = custom
  const [giftForm, setGiftForm] = useState({ amount: '', buyer_email: '', recipient_email: '', recipient_name: '', message: '' });
  const [giftSubmitting, setGiftSubmitting] = useState(false);
  const [giftChecking, setGiftChecking] = useState(false);
  const [giftResult, setGiftResult] = useState(null);     // { code, amount, recipient_email }

  useEffect(() => {
    if (preData) { setData(preData); setLoading(false); return; }
    axios.get(`${API}/business-page/${userId}`).then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, [userId, preData]);

  const loadAvailability = useCallback(() => {
    const d = date.toISOString().slice(0, 10);
    axios.get(`${API}/business-page/${userId}/availability`, { params: { date: d, specialist_id: specialistId || undefined, page_id: pageId || data?.id || undefined } })
      .then(({ data }) => { setTaken(data.taken || []); setDayAvailable(data.available !== false); })
      .catch(() => { setTaken([]); setDayAvailable(true); });
  }, [date, userId, specialistId, pageId, data?.id]);

  useEffect(() => { loadAvailability(); }, [loadAvailability]);

  // Returned from Stripe: poll booking payment, then invite a review.
  useEffect(() => {
    const sid = sp.get('session_id');
    if (sp.get('cancelled')) {
      toast.error('Maksājums atcelts');
      sp.delete('cancelled'); setSp(sp, { replace: true });
      return;
    }
    if (!sid || !sp.get('paid')) return;
    setPayChecking(true);
    let attempt = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/business-page/booking/status/${sid}`);
        if (data.payment_status === 'paid') {
          setPayChecking(false);
          toast.success('Maksājums saņemts! Rezervācija gaida apstiprinājumu.');
          sp.delete('paid'); sp.delete('session_id'); setSp(sp, { replace: true });
          return;
        }
      } catch { /* keep polling */ }
      attempt += 1;
      if (attempt >= 8) {
        setPayChecking(false);
        toast.message('Maksājums tiek apstrādāts', { description: 'Statuss atjaunosies drīzumā.' });
        sp.delete('paid'); sp.delete('session_id'); setSp(sp, { replace: true });
        return;
      }
      setTimeout(poll, 1500);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Returned from Stripe after a GIFT CARD purchase: poll, then reveal the code.
  useEffect(() => {
    const sid = sp.get('session_id');
    if (sp.get('gift_cancelled')) {
      toast.error('Maksājums atcelts');
      sp.delete('gift_cancelled'); setSp(sp, { replace: true });
      return;
    }
    if (!sid || !sp.get('gift_paid')) return;
    setGiftChecking(true);
    let attempt = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/business-page/gift-card/status/${sid}`);
        if (data.payment_status === 'paid') {
          setGiftChecking(false);
          setGiftResult({ code: data.code, amount: data.amount, recipient_email: data.recipient_email });
          setGiftOpen(true);
          toast.success('Dāvanu karte apmaksāta! Kods nosūtīts e-pastā.');
          sp.delete('gift_paid'); sp.delete('session_id'); setSp(sp, { replace: true });
          return;
        }
      } catch { /* keep polling */ }
      attempt += 1;
      if (attempt >= 8) {
        setGiftChecking(false);
        toast.message('Maksājums tiek apstrādāts', { description: 'Kods tiks nosūtīts e-pastā drīzumā.' });
        sp.delete('gift_paid'); sp.delete('session_id'); setSp(sp, { replace: true });
        return;
      }
      setTimeout(poll, 1500);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const rc = data?.reservations || {};
  const specialists = rc.specialists || [];
  const selectedSpecialist = specialists.find((s) => (s.id || '') === specialistId) || null;
  const isDayDisabled = (d) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (d < today) return true;
    if (!selectedSpecialist) return false;
    const iso = d.toISOString().slice(0, 10);
    if ((selectedSpecialist.off_dates || []).includes(iso)) return true;
    const wd = (d.getDay() + 6) % 7; // 0 = Mon
    const wds = selectedSpecialist.work_days || [];
    if (wds.length && !wds.includes(wd)) return true;
    return false;
  };
  const kind = rc.kind || 'general';
  const L = KIND_LABELS[kind] || KIND_LABELS.general;
  const requestOnly = !!rc.request_only;
  const availPlugins = pluginsFor('reservations', kind);
  const on = (id) => availPlugins.includes(id) && pluginEnabled('reservations', rc.plugins, id, kind);
  const secOrder = orderedSections('reservations', rc.module_order, kind);
  const ord = (id) => { const i = secOrder.indexOf(id); return i < 0 ? 99 : i; };
  // "Special" page: embedded restaurant engine (menu ordering + table reservation).
  const mc = data?.menu || {};
  const hasMenuContent = on('menu') && ((mc.categories?.length > 0) || !!mc.daily_special);
  const hasTables = on('tables') && !!mc.reservations_enabled;
  // Structured contact links (Par mums / Kontakti modules — superset of the business-card page).
  const cc = rc.contacts || {};
  const contactBtns = [
    cc.phone && { icon: Phone, label: 'Zvanīt', href: `tel:${cc.phone}`, testid: 'rc-contact-phone' },
    cc.email && { icon: Mail, label: 'E-pasts', href: `mailto:${cc.email}`, testid: 'rc-contact-email' },
    cc.website && { icon: Globe, label: 'Mājaslapa', href: cc.website.startsWith('http') ? cc.website : `https://${cc.website}`, testid: 'rc-contact-website' },
    cc.facebook && { icon: Facebook, label: 'Facebook', href: cc.facebook.startsWith('http') ? cc.facebook : `https://${cc.facebook}`, testid: 'rc-contact-facebook' },
    cc.instagram && { icon: Instagram, label: 'Instagram', href: cc.instagram.startsWith('http') ? cc.instagram : `https://${cc.instagram}`, testid: 'rc-contact-instagram' },
  ].filter(Boolean);
  const openBooking = (svc = null) => { setService(svc); setConfirmCode(null); setForm((f) => ({ name: f.name || user?.name || '', phone: f.phone || user?.phone || '', email: f.email || user?.email || '', note: f.note || '' })); setBookingOpen(true); };

  // Gift card purchase — open the form for a predefined card (idx) or custom amount.
  const openGift = (idx = null) => {
    setGiftIdx(idx);
    setGiftResult(null);
    const preset = idx != null && rc.gift_cards?.[idx] ? String(rc.gift_cards[idx].amount) : '';
    setGiftForm({ amount: preset, buyer_email: '', recipient_email: '', recipient_name: '', message: '' });
    setGiftOpen(true);
  };
  const submitGift = async () => {
    const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    if (!emailRe.test(giftForm.buyer_email.trim())) { toast.error('Ievadi derīgu pircēja e-pastu'); return; }
    if (!emailRe.test(giftForm.recipient_email.trim())) { toast.error('Ievadi derīgu saņēmēja e-pastu'); return; }
    const amt = giftIdx != null ? Number(rc.gift_cards[giftIdx].amount) : Number(giftForm.amount);
    if (giftIdx == null && (!amt || amt < 1 || amt > 1000)) { toast.error('Summai jābūt no €1 līdz €1000'); return; }
    setGiftSubmitting(true);
    try {
      const { data: res } = await axios.post(`${API}/business-page/${userId}/gift-card/purchase`, {
        page_id: pageId || data?.id || undefined,
        card_index: giftIdx != null ? giftIdx : undefined,
        amount: giftIdx == null ? amt : undefined,
        buyer_email: giftForm.buyer_email.trim(),
        recipient_email: giftForm.recipient_email.trim(),
        recipient_name: giftForm.recipient_name.trim() || undefined,
        message: giftForm.message.trim() || undefined,
        origin_url: window.location.origin + window.location.pathname,
      });
      if (res.checkout_url) { window.location.href = res.checkout_url; return; }
      toast.error('Neizdevās sākt maksājumu');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Neizdevās sākt maksājumu');
    } finally { setGiftSubmitting(false); }
  };

  const [contact, setContact] = useState({ name: '', email: '', phone: '', message: '' });
  const [contactSent, setContactSent] = useState(false);
  const [sendingContact, setSendingContact] = useState(false);
  const submitContact = async () => {
    if (!contact.name.trim() || !contact.message.trim()) { toast.error('Aizpildi vārdu un ziņu'); return; }
    setSendingContact(true);
    try {
      await axios.post(`${API}/business-page/${userId}/contact`, { ...contact, page_id: pageId || data?.id || undefined });
      setContactSent(true); setContact({ name: '', email: '', phone: '', message: '' });
      toast.success('Ziņa nosūtīta!');
    } catch { toast.error('Neizdevās nosūtīt'); } finally { setSendingContact(false); }
  };

  const [payHold, setPayHold] = useState(null);
  const submitBooking = async (payMethod) => {
    if (!requestOnly && !slot) { toast.error('Izvēlies laiku'); return; }
    if (!form.name.trim()) { toast.error('Ievadi vārdu'); return; }
    if (!form.phone.trim()) { toast.error('Norādi telefona numuru'); return; }
    if (!isValidPhone(form.phone)) { toast.error(PHONE_ERROR); return; }
    setSubmitting(true);
    try {
      const d = date.toISOString().slice(0, 10);
      const { data: resp } = await axios.post(`${API}/business-page/${userId}/booking`, {
        page_id: pageId || data?.id || undefined,
        pay_method: payRequired ? (payMethod || 'online') : undefined,
        service_id: service?.id, service_name: service?.name,
        specialist_id: specialistId || null, specialist_name: selectedSpecialist?.name || null,
        date: requestOnly ? '' : d, time_slot: requestOnly ? '' : slot, name: form.name, phone: form.phone, email: form.email, note: form.note,
        origin_url: window.location.origin,
      });
      // Client chose to pay online → hold the slot and show the live countdown.
      if (resp.requires_payment && resp.checkout_url) {
        setBookingOpen(false);
        setPayHold({
          url: resp.checkout_url,
          until: resp.hold_until ? new Date(resp.hold_until).getTime() : Date.now() + (resp.hold_minutes || 15) * 60000,
        });
        return;
      }
      toast.success(resp.pay_choice === 'external'
        ? 'Rezervācija nosūtīta! Apmaksā klātienē vai attālināti.'
        : (requestOnly ? 'Pieprasījums nosūtīts!' : 'Rezervācijas pieprasījums nosūtīts!'));
      setConfirmCode(true);
      setSlot(null); setForm({ name: '', phone: '', email: '', note: '' });
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Neizdevās nosūtīt');
      if (e?.response?.status === 409) { setSlot(null); loadAvailability?.(); }
    } finally { setSubmitting(false); }
  };

  const payRequired = !!(service && Number(service.price) > 0);
  const allowOnline = rc.allow_online !== false;
  const allowOnsite = !!rc.allow_onsite;

  const pv = pageVars(rc.surface, rc.accent, 'reservations');
  const hasCover = !!rc.cover_url;
  const heroTitle = hasCover ? '#FFFFFF' : 'var(--text-primary)';
  const heroSub = hasCover ? 'rgba(255,255,255,0.78)' : 'var(--text-muted)';
  const optStyle = { background: 'var(--bg-surface-secondary)', color: 'var(--text-primary)' };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;
  if (!data || data.template !== 'reservations') {
    return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white/60" data-testid="business-page-empty">Šai lapai nav aktīva rezervāciju šablona.</div>;
  }

  const slots = rc.time_slots?.length ? rc.time_slots : ['10:00', '12:00', '14:00', '16:00', '18:00'];

  return (
    <div className="min-h-screen pb-28" style={{ ...pv, background: 'var(--bg-page)', color: 'var(--text-primary)', fontFamily: 'Manrope, sans-serif' }} data-testid="business-reservations-page">
      {payHold && <PaymentHoldOverlay url={payHold.url} until={payHold.until} onClose={() => setPayHold(null)} />}
      {/* Hero */}
      <div className="relative h-[52vh] min-h-[340px] overflow-hidden">
        {hasCover
          ? <img src={resolveAssetUrl(rc.cover_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
          : <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 90% at 70% 0%, var(--accent-ring), var(--bg-page) 60%)' }} />}
        <div className="absolute inset-0" style={{ background: hasCover
          ? 'linear-gradient(to top, var(--bg-page) 0%, rgba(0,0,0,0.55) 30%, rgba(0,0,0,0.15) 70%, transparent 100%)'
          : 'linear-gradient(to top, var(--bg-page) 2%, transparent 58%)' }} />
        <div className="relative h-full max-w-5xl mx-auto px-5 flex flex-col justify-end pb-10">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="text-[11px] font-bold uppercase tracking-[0.22em] text-[var(--accent)] mb-3 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> {data.owner?.region || 'Latvija'}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="text-4xl sm:text-6xl font-light tracking-tighter" style={{ color: heroTitle, fontFamily: 'Outfit, sans-serif' }}>
            {data.owner?.name}
          </motion.h1>
          {rc.tagline && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.12 }}
            className="text-base sm:text-lg mt-3 max-w-xl" style={{ color: heroSub }}>{rc.tagline}</motion.p>}
          {on('calendar') && (
          <motion.button initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
            onClick={() => openBooking(null)}
            className="mt-6 self-start inline-flex items-center gap-2 h-12 px-8 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] transition-transform hover:scale-105"
            style={{ boxShadow: '0 10px 40px var(--accent-ring)' }} data-testid="hero-book-btn">
            <CalendarClock className="w-5 h-5" /> {L.cta}
          </motion.button>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 flex flex-col gap-12 sm:gap-14 mt-12">
        <section data-testid="contact-business-section" className="flex" style={{ order: -2 }}>
          <ContactBusinessButton ownerId={userId} ownerName={data.owner?.name} />
        </section>
        {/* Pricing */}
        {rc.services?.length > 0 && (
          <section data-testid="pricing-section" style={{ order: ord('services') }}>
            <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-5" style={{ fontFamily: 'Outfit' }}>{L.price}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {rc.services.map((s) => { const imgs = itemImages(s); return (
                <div key={s.id} className="rounded-2xl border border-[var(--border-card)] bg-[var(--bg-card)] p-4 sm:p-6 shadow-sm hover:border-[var(--accent-border)] transition-colors flex flex-col" data-testid={`price-card-${s.id}`}>
                  {imgs.length > 0 && (
                    <div className="mb-3 sm:mb-4">
                      <img src={resolveAssetUrl(imgs[0])} alt={s.name} onClick={() => setLightbox({ images: imgs, index: 0 })} className="w-full h-28 sm:h-40 rounded-xl object-cover cursor-zoom-in" data-testid={`price-image-${s.id}`} />
                      {imgs.length > 1 && (
                        <div className="flex gap-1.5 mt-1.5 flex-wrap">
                          {imgs.slice(1).map((u, k) => (
                            <img key={k} src={resolveAssetUrl(u)} alt="" onClick={() => setLightbox({ images: imgs, index: k + 1 })} className="w-10 h-10 sm:w-12 sm:h-12 rounded-md object-cover cursor-zoom-in flex-shrink-0 border border-[var(--border-card)] hover:border-[var(--accent)] transition-colors" data-testid={`price-thumb-${s.id}-${k + 1}`} />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <h3 className="text-[var(--text-primary)] font-semibold text-base sm:text-lg leading-snug" style={{ fontFamily: 'Outfit' }}>{s.name}</h3>
                  {s.description && <p className="text-[var(--text-faint)] text-xs sm:text-sm mt-1">{s.description}</p>}
                  <div className="flex items-baseline gap-1 mt-auto pt-3">
                    {Number(s.price) > 0 ? (
                      <>
                        <span className="text-2xl sm:text-3xl font-light text-[var(--accent)]">€{fmtEur(s.price)}</span>
                        <span className="text-[var(--text-faint)] text-xs sm:text-sm">{UNIT_LV[s.unit] || ''}</span>
                      </>
                    ) : (
                      <span className="text-lg sm:text-xl font-light text-[var(--accent)]">Pēc vienošanās</span>
                    )}
                  </div>
                  {s.duration_min ? <p className="text-[var(--text-faint)] text-xs mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> {s.duration_min} min</p> : null}
                  <button onClick={() => openBooking(s)} className="mt-3 sm:mt-4 w-full h-10 rounded-full bg-[var(--bg-input)] border border-[var(--border-input)] text-[var(--text-primary)] text-xs sm:text-sm font-semibold hover:bg-[var(--accent)] hover:text-[var(--accent-contrast)] hover:border-[var(--accent)] transition-colors" data-testid={`book-service-${s.id}`}>
                    {L.cta}
                  </button>
                </div>
              ); })}
            </div>
          </section>
        )}

        {/* Par mums (About) — superset module from the business-card page */}
        {on('about') && rc.about && (
          <section data-testid="about-section" style={{ order: ord('about') }}>
            <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-4" style={{ fontFamily: 'Outfit' }}>Par mums</h2>
            <p className="text-[var(--text-muted)] leading-relaxed whitespace-pre-line">{rc.about}</p>
          </section>
        )}

        {/* Kontakti (structured contact links) */}
        {on('contacts') && contactBtns.length > 0 && (
          <section data-testid="contacts-section" style={{ order: ord('contacts') }}>
            <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-5" style={{ fontFamily: 'Outfit' }}>Kontakti</h2>
            <div className="flex flex-wrap gap-3">
              {contactBtns.map((b) => (
                <a key={b.testid} href={b.href} target={b.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[var(--bg-card)] border border-[var(--border-card)] text-[var(--text-primary)] text-sm font-semibold hover:bg-[var(--accent)] hover:border-[var(--accent)] hover:text-[var(--accent-contrast)] transition-colors"
                  data-testid={b.testid}>
                  <b.icon className="w-4 h-4" /> {b.label}
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Special page: embedded Ēdienkarte & Galdiņu rezervācija (restaurant engine) */}
        {(hasMenuContent || hasTables) && (
          <section data-testid="special-menu-section" style={{ order: ord('menu') }}>
            <BusinessMenuPage
              data={data}
              userId={userId}
              pageId={pageId || data?.id}
              embedded
              showMenu={hasMenuContent}
              showTables={hasTables}
              surfaceId={rc.surface}
              accentId={rc.accent}
            />
          </section>
        )}

        {/* Promotions + Working hours & map (optional modules) */}
        {on('promotions') && <PromotionsSection promotions={rc.promotions} order={ord('promotions')} />}
        <HoursMapSection showHours={on('hours')} hours={rc.working_hours} showMap={on('map')} address={rc.address} lat={rc.lat} lng={rc.lng} order={ord('hours_map')} />

        {/* Gift cards */}
        {on('gift_cards') && (
          <section data-testid="giftcards-section" style={{ order: ord('gift_cards') }}>
            <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-5" style={{ fontFamily: 'Outfit' }}>Dāvanu kartes</h2>
            <div className="flex flex-wrap gap-4">
              {(rc.gift_cards || []).map((g, i) => (
                <div key={i} className="rounded-2xl p-5 min-w-[210px] relative overflow-hidden border border-[var(--accent-border)] shadow-sm flex flex-col"
                  style={{ background: 'linear-gradient(135deg, var(--accent-soft), transparent)' }} data-testid={`giftcard-${i}`}>
                  <Gift className="w-6 h-6 text-[var(--accent)] mb-3" />
                  <p className="text-[var(--text-primary)] font-semibold">{g.name}</p>
                  <p className="text-2xl font-light text-[var(--accent)] mt-1 mb-4">€{g.amount}</p>
                  <button onClick={() => openGift(i)}
                    className="mt-auto inline-flex items-center justify-center gap-2 h-10 px-5 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] text-sm font-semibold hover:bg-[var(--accent-hover)] transition-colors"
                    data-testid={`giftcard-buy-${i}`}>
                    <Gift className="w-4 h-4" /> Pirkt
                  </button>
                </div>
              ))}
              {/* Custom amount */}
              <button onClick={() => openGift(null)}
                className="rounded-2xl p-5 min-w-[210px] border border-dashed border-[var(--accent-border)] bg-[var(--bg-card)] hover:bg-[var(--accent-soft)] transition-colors text-left flex flex-col"
                data-testid="giftcard-custom">
                <Gift className="w-6 h-6 text-[var(--accent)] mb-3" />
                <p className="text-[var(--text-primary)] font-semibold">Cita summa</p>
                <p className="text-sm text-[var(--text-faint)] mt-1 mb-4">Izvēlies dāvanas vērtību pats</p>
                <span className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-[var(--accent)]">
                  <CreditCard className="w-4 h-4" /> Pirkt dāvanu karti
                </span>
              </button>
            </div>
          </section>
        )}

        {/* Gallery */}
        {on('gallery') && rc.gallery?.length > 0 && (
          <section data-testid="gallery-section" style={{ order: ord('gallery') }}>
            <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-5" style={{ fontFamily: 'Outfit' }}>Galerija</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {rc.gallery.map((g, i) => (
                <img key={i} src={resolveAssetUrl(g)} alt="" onClick={() => setLightbox({ images: rc.gallery, index: i })} className={`w-full rounded-2xl object-cover cursor-zoom-in ${i % 3 === 0 ? 'h-64 row-span-2' : 'h-32'}`} data-testid={`gallery-image-${i}`} />
              ))}
            </div>
          </section>
        )}

        {/* Specialists / masters */}
        {on('specialists') && (specialists.length > 0) && (
          <section data-testid="specialists-section" style={{ order: ord('specialists') }}>
            <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-2" style={{ fontFamily: 'Outfit' }}>Mūsu speciālisti</h2>
            {!requestOnly && <p className="text-[var(--text-faint)] text-sm mb-5">Izvēlies speciālistu, pie kura vēlies pierakstīties (neobligāti).</p>}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {!requestOnly && (
                <button
                  type="button"
                  onClick={() => setSpecialistId('')}
                  className={`rounded-2xl border p-4 text-center transition-colors ${specialistId === '' ? 'border-[var(--accent)] bg-[var(--accent-soft)]' : 'border-[var(--border-card)] bg-[var(--bg-card)] hover:border-[var(--accent-border)]'}`}
                  data-testid="specialist-any">
                  <div className="w-20 h-20 mx-auto rounded-full overflow-hidden bg-[var(--bg-input)] mb-3 flex items-center justify-center">
                    <User className="w-8 h-8 text-[var(--text-faint)]" />
                  </div>
                  <p className="text-[var(--text-primary)] font-semibold text-sm truncate">Jebkurš speciālists</p>
                  <p className="text-[var(--text-faint)] text-xs mt-0.5">Bez izvēles</p>
                </button>
              )}
              {specialists.map((spc, i) => {
                const active = !requestOnly && specialistId === (spc.id || '');
                const Wrap = requestOnly ? 'div' : 'button';
                return (
                  <Wrap
                    key={spc.id || i}
                    type={requestOnly ? undefined : 'button'}
                    onClick={requestOnly ? undefined : () => setSpecialistId(spc.id || '')}
                    className={`rounded-2xl border p-4 text-center transition-colors ${active ? 'border-[var(--accent)] bg-[var(--accent-soft)]' : 'border-[var(--border-card)] bg-[var(--bg-card)]'} ${requestOnly ? '' : 'hover:border-[var(--accent-border)] cursor-pointer'}`}
                    data-testid={`specialist-${spc.id || i}`}>
                    <div className="w-20 h-20 mx-auto rounded-full overflow-hidden bg-[var(--bg-input)] mb-3 flex items-center justify-center">
                      {spc.photo_url ? <img src={spc.photo_url} alt={spc.name} className="w-full h-full object-cover" /> : <User className="w-8 h-8 text-[var(--text-faint)]" />}
                    </div>
                    <p className="text-[var(--text-primary)] font-semibold text-sm truncate">{spc.name}</p>
                    {spc.role && <p className="text-[var(--text-faint)] text-xs mt-0.5 truncate">{spc.role}</p>}
                  </Wrap>
                );
              })}
            </div>
          </section>
        )}

        {/* Booking calendar inline (or a request CTA for request-only kinds) */}
        {on('calendar') && (
        <section data-testid="booking-section" style={{ order: -1 }}>
          <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-5" style={{ fontFamily: 'Outfit' }}>{L.section}</h2>
          {requestOnly ? (
            <div className="rounded-3xl border border-[var(--border-card)] bg-[var(--bg-card)] p-8 text-center shadow-sm">
              <p className="text-[var(--text-muted)] text-sm mb-5 max-w-md mx-auto">
                {rc.contact_note || 'Atstāj savu pieprasījumu un mēs ar tevi sazināsimies, lai apstiprinātu detaļas.'}
              </p>
              <button onClick={() => openBooking(null)}
                className="inline-flex items-center gap-2 h-12 px-8 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] transition-colors"
                data-testid="request-btn">
                <CalendarClock className="w-5 h-5" /> {L.cta}
              </button>
            </div>
          ) : (
          <div className="rounded-3xl border border-[var(--border-card)] bg-[var(--bg-card)] p-6 shadow-sm flex flex-col md:flex-row gap-8">
            <div className="[&_.rdp]:text-[var(--text-primary)] [&_.rdp-day]:text-[var(--text-muted)] flex justify-center">
              <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} disabled={isDayDisabled} className="text-[var(--text-primary)]" />
            </div>
            <div className="flex-1">
              {selectedSpecialist && <p className="text-[var(--accent)] text-xs font-semibold mb-2" data-testid="selected-specialist-label">Pieraksts pie: {selectedSpecialist.name}</p>}
              <p className="text-[var(--text-muted)] text-sm mb-3">Pieejamie laiki {date?.toLocaleDateString('lv-LV')}:</p>
              {!dayAvailable ? (
                <p className="text-[var(--text-faint)] text-sm rounded-xl bg-[var(--bg-input)] border border-[var(--border-input)] px-4 py-3" data-testid="specialist-off-note">
                  {selectedSpecialist ? `${selectedSpecialist.name} šajā dienā nestrādā. Izvēlies citu datumu vai speciālistu.` : 'Šajā dienā nav pieejamu laiku.'}
                </p>
              ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {slots.map((s) => (
                  <button key={s} disabled={taken.includes(s)} onClick={() => { setSlot(s); openBooking(null); }}
                    className={`min-h-[44px] rounded-xl text-sm font-semibold border transition-colors disabled:opacity-40 disabled:line-through disabled:cursor-not-allowed ${slot === s ? 'bg-[var(--accent)] text-[var(--accent-contrast)] border-[var(--accent)]' : 'border-[var(--border-input)] text-[var(--text-primary)] hover:border-[var(--accent-border)]'}`}
                    data-testid={`slot-${s}`}>{s}</button>
                ))}
              </div>
              )}
              {rc.contact_note && <p className="text-[var(--text-faint)] text-sm mt-5 flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 shrink-0" /> {rc.contact_note}</p>}
            </div>
          </div>
          )}
        </section>
        )}

        {/* FAQ */}
        {on('faq') && rc.faq?.length > 0 && (
          <section data-testid="faq-section" style={{ order: ord('faq') }}>
            <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-5 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><HelpCircle className="w-5 h-5 text-[var(--accent)]" /> Biežāk uzdotie jautājumi</h2>
            <div className="space-y-3">
              {rc.faq.map((f, i) => (
                <details key={i} className="group rounded-2xl border border-[var(--border-card)] bg-[var(--bg-card)] px-5 py-4 shadow-sm" data-testid={`faq-item-${i}`}>
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-3 text-[var(--text-primary)] font-medium">
                    {f.q}
                    <span className="text-[var(--accent)] text-xl leading-none transition-transform group-open:rotate-45">+</span>
                  </summary>
                  {f.a && <p className="text-[var(--text-muted)] text-sm mt-3 leading-relaxed">{f.a}</p>}
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Reviews */}
        {on('reviews') && rc.reviews?.length > 0 && (
          <section data-testid="reviews-section" style={{ order: ord('reviews') }}>
            <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-5 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Star className="w-5 h-5 text-[var(--accent)]" /> Atsauksmes</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {rc.reviews.map((r, i) => (
                <div key={i} className="rounded-2xl border border-[var(--border-card)] bg-[var(--bg-card)] p-5 shadow-sm" data-testid={`review-${i}`}>
                  <div className="flex items-center gap-0.5 mb-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-4 h-4 ${n <= (r.rating || 5) ? 'text-[var(--accent)] fill-[var(--accent)]' : 'text-[var(--text-faint)]'}`} />
                    ))}
                  </div>
                  {r.text && <p className="text-[var(--text-muted)] text-sm leading-relaxed">“{r.text}”</p>}
                  <p className="text-[var(--text-faint)] text-xs mt-3 font-semibold">{r.author}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Contact form */}
        {on('contact_form') && (
          <section data-testid="contact-section" style={{ order: ord('contact') }}>
            <h2 className="text-2xl font-medium text-[var(--text-primary)] mb-5 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Mail className="w-5 h-5 text-[var(--accent)]" /> Sazinies ar mums</h2>
            <div className="rounded-3xl border border-[var(--border-card)] bg-[var(--bg-card)] p-6 max-w-2xl shadow-sm">
              {contactSent ? (
                <div className="text-center py-6" data-testid="contact-sent">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-500/20 flex items-center justify-center"><Check className="w-6 h-6 text-emerald-400" /></div>
                  <p className="text-[var(--text-primary)] font-semibold">Paldies! Ziņa nosūtīta.</p>
                  <p className="text-[var(--text-faint)] text-sm mt-1">Mēs ar tevi sazināsimies pēc iespējas ātrāk.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Vārds *" className={INPUT_CLS} data-testid="contact-name" />
                    <Input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="Tālrunis" className={INPUT_CLS} data-testid="contact-phone" />
                  </div>
                  <Input value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="E-pasts" className={INPUT_CLS} data-testid="contact-email" />
                  <Textarea value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} placeholder="Tava ziņa *" rows={4} className={INPUT_CLS} data-testid="contact-message" />
                  <button onClick={submitContact} disabled={sendingContact}
                    className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] disabled:opacity-60" data-testid="contact-submit">
                    {sendingContact ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Nosūtīt ziņu
                  </button>
                </div>
              )}
            </div>
          </section>
        )}
      </div>

      {/* Sticky mobile CTA */}
      {on('calendar') && (
      <div className="fixed bottom-0 inset-x-0 z-40 p-3 backdrop-blur-xl border-t border-[var(--border-card)] md:hidden" style={{ ...pv, background: 'var(--bg-header)', paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
        <button onClick={() => openBooking(null)} className="w-full h-12 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold flex items-center justify-center gap-2" data-testid="sticky-book-btn">
          <CalendarClock className="w-5 h-5" /> {L.cta}
        </button>
      </div>
      )}

      <ImageLightbox value={lightbox} onClose={() => setLightbox(null)} />

      {/* Booking dialog */}
      <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
        <DialogContent style={{ ...pv, background: 'var(--bg-surface-secondary)', borderColor: 'var(--border-card)' }} className="text-[var(--text-primary)] max-h-[88vh] overflow-y-auto w-[92vw] sm:w-full max-w-lg" data-testid="booking-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Outfit' }}>{L.dialog}</DialogTitle>
            <DialogDescription className="text-[var(--text-muted)]">{requestOnly ? 'Atstāj savus kontaktus un mēs ar tevi sazināsimies.' : 'Izvēlies datumu un laiku, un mēs nosūtīsim pieprasījumu biznesam.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {confirmCode ? (
              <div className="text-center py-4" data-testid="booking-confirmation">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <Check className="w-7 h-7 text-emerald-400" />
                </div>
                <p className="text-[var(--text-primary)] font-semibold mb-1">Pieprasījums nosūtīts!</p>
                <p className="text-[var(--text-faint)] text-sm mb-4">Rezervācija gaida uzņēmuma apstiprinājumu. Kad tā tiks apstiprināta, saņemsi apstiprinājuma kodu{form.email ? ' e-pastā' : ''}, ko uzrādīt ierodoties.</p>
                <button onClick={() => { setConfirmCode(null); setBookingOpen(false); }} className="w-full h-11 rounded-full bg-[var(--bg-card)] border border-[var(--border-card)] text-[var(--text-primary)] font-semibold hover:opacity-80" data-testid="booking-done">Aizvērt</button>
              </div>
            ) : (
            <>
            {service && <p className="text-[var(--accent)] text-sm font-semibold">{service.name} · €{service.price}</p>}
            {payRequired && (
              <div className="rounded-xl bg-[var(--accent-soft)] border border-[var(--accent-border)] px-3 py-2 text-[var(--text-muted)] text-xs" data-testid="booking-payment-note">
                Cena €{Number(service.price).toFixed(2)}. {allowOnline && allowOnsite ? 'Izvēlies, kā maksāt: tagad online ar karti caur KoLV, vai klātienē/attālināti.' : allowOnline ? 'Apmaksa online ar karti caur KoLV.' : 'Apmaksa klātienē vai attālināti.'} {allowOnline ? 'Atsauksmi var atstāt pēc online apmaksas.' : ''}
              </div>
            )}
            {!requestOnly && specialists.length > 0 && (
              <select value={specialistId} onChange={(e) => setSpecialistId(e.target.value)} className="w-full h-11 rounded-xl bg-[var(--bg-input)] border border-[var(--border-input)] text-[var(--text-primary)] text-sm px-3" data-testid="dialog-specialist-select">
                <option value="" style={optStyle}>Jebkurš speciālists</option>
                {specialists.map((spc) => <option key={spc.id} value={spc.id} style={optStyle}>{spc.name}{spc.role ? ` — ${spc.role}` : ''}</option>)}
              </select>
            )}
            {!requestOnly && (
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-xl bg-[var(--bg-input)] px-3 py-2 text-sm text-[var(--text-muted)]">{date?.toLocaleDateString('lv-LV')}</div>
              <select value={slot || ''} onChange={(e) => setSlot(e.target.value)} className="h-11 rounded-xl bg-[var(--bg-input)] border border-[var(--border-input)] text-[var(--text-primary)] text-sm px-3" data-testid="dialog-slot-select">
                <option value="" style={optStyle}>Izvēlies laiku</option>
                {slots.map((s) => <option key={s} value={s} disabled={taken.includes(s)} style={optStyle}>{s}{taken.includes(s) ? ' — aizņemts' : ''}</option>)}
              </select>
            </div>
            )}
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Tavs vārds" className={INPUT_CLS} data-testid="booking-name" />
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Telefons *" className={INPUT_CLS} data-testid="booking-phone" />
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="E-pasts (lai saņemtu apstiprinājumu)" className={INPUT_CLS} data-testid="booking-email" />
            <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Piezīme (neobligāti)" className={INPUT_CLS} data-testid="booking-note" />
            {payRequired ? (
              <div className="space-y-2">
                {allowOnline && (
                  <button onClick={() => submitBooking('online')} disabled={submitting}
                    className="w-full h-11 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] disabled:opacity-60 flex items-center justify-center gap-2" data-testid="submit-booking-online">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Maksāt tagad €{Number(service.price).toFixed(2)}
                  </button>
                )}
                {allowOnsite && (
                  <button onClick={() => submitBooking('external')} disabled={submitting}
                    className={`w-full h-11 rounded-full disabled:opacity-60 flex items-center justify-center gap-2 font-semibold ${allowOnline ? 'border border-[var(--border-card)] text-[var(--text-primary)] hover:bg-[var(--bg-card)]' : 'bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)]'}`} data-testid="submit-booking-external">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Maksāt klātienē / attālināti
                  </button>
                )}
              </div>
            ) : (
              <button onClick={() => submitBooking()} disabled={submitting}
                className="w-full h-11 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] disabled:opacity-60 flex items-center justify-center gap-2" data-testid="submit-booking">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Nosūtīt pieprasījumu
              </button>
            )}
            </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment verification overlay (returning from Stripe) */}
      {payChecking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm" style={{ background: 'var(--bg-overlay)' }} data-testid="pay-checking">
          <div className="flex flex-col items-center gap-3 text-[var(--text-primary)]">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--accent)]" />
            <p className="text-sm">Apstiprinām maksājumu…</p>
          </div>
        </div>
      )}
      {giftChecking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm" style={{ background: 'var(--bg-overlay)' }} data-testid="gift-checking">
          <div className="flex flex-col items-center gap-3 text-[var(--text-primary)]">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--accent)]" />
            <p className="text-sm">Apstiprinām maksājumu…</p>
          </div>
        </div>
      )}

      {/* Gift card purchase / success dialog */}
      <Dialog open={giftOpen} onOpenChange={setGiftOpen}>
        <DialogContent style={{ ...pv, background: 'var(--bg-surface-secondary)', borderColor: 'var(--border-card)' }} className="text-[var(--text-primary)] max-h-[88vh] overflow-y-auto w-[92vw] sm:w-full max-w-md" data-testid="gift-dialog">
          {giftResult ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Check className="w-5 h-5 text-[var(--accent)]" /> Dāvanu karte apmaksāta!</DialogTitle>
                <DialogDescription className="text-[var(--text-faint)]">Kods ir nosūtīts uz {giftResult.recipient_email}.</DialogDescription>
              </DialogHeader>
              <div className="rounded-2xl p-6 text-center border border-[var(--accent-border)]" style={{ background: 'linear-gradient(135deg, var(--accent-soft), transparent)' }}>
                <Gift className="w-8 h-8 text-[var(--accent)] mx-auto mb-2" />
                <p className="text-sm text-[var(--text-faint)]">Dāvanu kartes vērtība</p>
                <p className="text-3xl font-light text-[var(--accent)] mb-3">€{Number(giftResult.amount).toFixed(2)}</p>
                <p className="text-sm text-[var(--text-faint)]">Kods</p>
                <p className="text-2xl font-mono font-bold tracking-widest text-[var(--text-primary)]" data-testid="gift-result-code">{giftResult.code}</p>
              </div>
              {giftResult.code && (
                <a href={`/davanu-karte/${giftResult.code}`} target="_blank" rel="noreferrer"
                  className="mt-3 inline-flex items-center justify-center gap-2 h-11 w-full rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] transition-colors" data-testid="gift-view-link">
                  <Gift className="w-4 h-4" /> Apskatīt un izdrukāt dāvanu karti
                </a>
              )}
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Gift className="w-5 h-5 text-[var(--accent)]" /> Pirkt dāvanu karti</DialogTitle>
                <DialogDescription className="text-[var(--text-faint)]">
                  {giftIdx != null && rc.gift_cards?.[giftIdx]
                    ? `${rc.gift_cards[giftIdx].name} — €${Number(rc.gift_cards[giftIdx].amount).toFixed(2)}`
                    : 'Izvēlies summu un ievadi saņēmēja e-pastu — kods tiks nosūtīts pēc apmaksas.'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {giftIdx == null && (
                  <div>
                    <label className="text-xs text-[var(--text-faint)]">Summa (€1–€1000)</label>
                    <Input type="number" min="1" max="1000" value={giftForm.amount}
                      onChange={(e) => setGiftForm({ ...giftForm, amount: e.target.value })}
                      placeholder="Piem. 25" className={INPUT_CLS} data-testid="gift-amount" />
                  </div>
                )}
                <Input type="email" value={giftForm.buyer_email} onChange={(e) => setGiftForm({ ...giftForm, buyer_email: e.target.value })} placeholder="Tavs e-pasts (čekam) *" className={INPUT_CLS} data-testid="gift-buyer-email" />
                <Input type="email" value={giftForm.recipient_email} onChange={(e) => setGiftForm({ ...giftForm, recipient_email: e.target.value })} placeholder="Saņēmēja e-pasts (kur sūtīt kodu) *" className={INPUT_CLS} data-testid="gift-recipient-email" />
                <Input value={giftForm.recipient_name} onChange={(e) => setGiftForm({ ...giftForm, recipient_name: e.target.value })} placeholder="Saņēmēja vārds (neobligāti)" className={INPUT_CLS} data-testid="gift-recipient-name" />
                <Textarea value={giftForm.message} onChange={(e) => setGiftForm({ ...giftForm, message: e.target.value })} placeholder="Personīga ziņa (neobligāti)" className={INPUT_CLS} data-testid="gift-message" />
                <button onClick={submitGift} disabled={giftSubmitting}
                  className="w-full h-11 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] font-bold hover:bg-[var(--accent-hover)] disabled:opacity-60 flex items-center justify-center gap-2" data-testid="gift-submit">
                  {giftSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  Maksāt {giftIdx != null && rc.gift_cards?.[giftIdx] ? `€${Number(rc.gift_cards[giftIdx].amount).toFixed(2)}` : (giftForm.amount ? `€${Number(giftForm.amount).toFixed(2)}` : '')}
                </button>
                <p className="text-[11px] text-[var(--text-faint)] text-center">Maksājums tiek apstrādāts droši caur KoLV (Stripe).</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
